import { Link } from 'react-router-dom'
import { ArrowRight, Pause, Play, Sparkles } from 'lucide-react'
import type { Campaign, ScheduleItem, Target } from '@/api/client'
import { useDraftCounts } from '@/lib/queries'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { dayKey } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Button, PlatformMark, StatusPill } from '@/ui'
import { PostLine, byAttention } from '../schedule/PostLine'
import { cadence, enabledPlatforms, modeLabel } from './shared'

/** How many of a campaign's posts the card lists before 캠페인 열기. */
const SHOWN_POSTS = 3

/** The days the activity strip covers, today last. */
export const STRIP_DAYS = 14

const DAY_MS = 86_400_000

/** One count in its box, opening the list it counts. Zero is greyed. */
function CountLink({ label, n, tone, to }: { label: string; n: number; tone?: Tone; to: string }) {
  return (
    <Link to={to} className="rounded-md bg-muted/60 px-2 py-1.5 text-center transition-colors hover:bg-muted">
      <div className={cn('text-body font-semibold tabular-nums', n === 0 ? 'text-muted-foreground/60' : tone && TONE_CLASS[tone].text)}>{n}</div>
      <div className="text-micro text-muted-foreground">{label}</div>
    </Link>
  )
}

/** The last read reaction of a post, by key (views, likes, replies…). */
const reaction = (it: ScheduleItem, key: string) => {
  const v = it.engagement?.[key]
  return typeof v === 'number' ? v : 0
}

/** What a campaign put up over the last days: posts per day as bars, and the
 *  week's posts with the views and likes they drew. */
export function weekOf(posts: ScheduleItem[], now: Date) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (STRIP_DAYS - 1))
  const days = Array.from({ length: STRIP_DAYS }, (_, i) => {
    const d = new Date(start.getTime() + i * DAY_MS)
    return { key: dayKey(d), date: d, n: 0 }
  })
  const weekFrom = now.getTime() - 7 * DAY_MS
  let posted = 0
  let views = 0
  let likes = 0
  for (const it of posts) {
    if (it.status !== 'published' || !it.published_at) continue
    const at = new Date(it.published_at)
    const day = days.find((d) => d.key === dayKey(at))
    if (day) day.n++
    if (at.getTime() >= weekFrom) {
      posted++
      views += reaction(it, 'views')
      likes += reaction(it, 'likes')
    }
  }
  return { days, posted, views, likes }
}

function Strip({ days, name }: { days: { key: string; date: Date; n: number }[]; name: string }) {
  const max = Math.max(1, ...days.map((d) => d.n))
  const total = days.reduce((a, d) => a + d.n, 0)
  return (
    <div role="img" aria-label={`${name} 최근 ${STRIP_DAYS}일 게시 ${total}건`} className="flex h-8 items-end gap-[3px]">
      {days.map((d) => (
        <span
          key={d.key}
          title={`${d.date.getMonth() + 1}. ${d.date.getDate()}. ${d.n}건`}
          className={cn('flex-1 rounded-sm', d.n > 0 ? TONE_CLASS.published.dot : 'bg-muted')}
          style={{ height: d.n > 0 ? `${Math.max(25, (d.n / max) * 100)}%` : '12%' }}
        />
      ))}
    </div>
  )
}

/** One campaign on the board: its pace and accounts, its posts by state (each
 *  count opens its list), the last two weeks as a strip, and the posts that
 *  need a look or go out next. */
export function CampaignCard({
  c,
  targets,
  posts,
  postsLoading,
  now,
  onGenerate,
  onToggle,
  toggling,
}: {
  c: Campaign
  targets: Target[]
  /** The campaign's posts from the schedule: booked, waiting and the last 30 days. */
  posts: ScheduleItem[]
  postsLoading: boolean
  now: Date
  onGenerate: () => void
  onToggle: () => void
  toggling: boolean
}) {
  const counts = useDraftCounts({ campaignId: c.ID }).data?.counts ?? {}
  const num = (...keys: string[]) => keys.reduce((a, k) => a + (counts[k] ?? 0), 0)
  const query = `campaign_id=${c.ID}`
  const platforms = enabledPlatforms(c)
  const week = weekOf(posts, now)
  const lines = posts.slice().sort(byAttention)
  const active = c.Status === 'active'

  return (
    <article aria-label={c.Name} className={cn('flex w-full flex-col gap-4 rounded-lg border border-border bg-card p-4', !active && 'opacity-90')}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link to={`/campaigns/${c.ID}`} className="text-body font-semibold hover:underline">
              {c.Name}
            </Link>
            <StatusPill domain="app" status={c.Status} />
          </div>
          <div className="text-meta text-muted-foreground">
            <span>{modeLabel(c.Mode)}</span>
            {' · '}
            <span>{cadence(c)}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {/* A Coupang campaign writes only from signals. */}
          {c.Mode !== 'affiliate' && (
            <Button size="sm" icon={Sparkles} onClick={onGenerate}>
              AI 초안 쓰기
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={active ? Pause : Play} loading={toggling} onClick={onToggle}>
            {active ? '멈춤' : '재개'}
          </Button>
        </div>
      </header>

      <section aria-label="계정" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-table">
        {platforms.length === 0 ? (
          <span className="text-muted-foreground">켠 플랫폼 없음</span>
        ) : (
          platforms.map((p) => {
            const t = targets.find((x) => x.id === c.PlatformConfigs[p]?.target_id)
            return (
              <span key={p} className="inline-flex items-center gap-1.5">
                <PlatformMark platform={p} />
                {t ? <span>{t.name}</span> : <span className={TONE_CLASS.publishing.text}>계정 미지정</span>}
              </span>
            )
          })
        )}
      </section>

      <section aria-label="글 현황" className="grid grid-cols-4 gap-1.5">
        <CountLink label="검토 대기" n={num('pending_review')} tone="review" to={`/review?${query}`} />
        <CountLink label="예약" n={num('approved', 'scheduled')} tone="scheduled" to={`/schedule?${query}`} />
        <CountLink label="게시됨" n={num('published')} to={`/results?${query}`} />
        <CountLink label="실패" n={num('failed')} tone="failed" to={`/schedule?${query}&status=failed`} />
      </section>

      <section aria-label="최근 게시" className="space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <span className="text-meta font-medium text-muted-foreground">최근 {STRIP_DAYS}일</span>
          <span className="text-meta tabular-nums text-muted-foreground">
            7일 게시 <span className="font-medium text-foreground">{week.posted}</span>
            {week.views > 0 && <> · 조회 {week.views.toLocaleString()}</>}
            {week.likes > 0 && <> · 좋아요 {week.likes.toLocaleString()}</>}
          </span>
        </div>
        <Strip days={week.days} name={c.Name} />
      </section>

      <section aria-label="글" className="flex flex-1 flex-col gap-1.5">
        {lines.length > 0 ? (
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
            {lines.slice(0, SHOWN_POSTS).map((it) => (
              <PostLine key={`${it.kind}-${it.id}`} it={it} />
            ))}
          </ul>
        ) : (
          !postsLoading && (
            <p className="rounded-md border border-dashed border-border px-2.5 py-3 text-center text-meta text-muted-foreground">
              최근 30일과 앞으로 잡힌 글이 없다
            </p>
          )
        )}
        <Link
          to={`/campaigns/${c.ID}`}
          className="mt-auto inline-flex items-center gap-1 self-start pt-1 text-meta font-medium text-accent hover:underline"
        >
          캠페인 열기
          <ArrowRight size={12} aria-hidden />
        </Link>
      </section>
    </article>
  )
}
